/**
 * Query/hash params that must not be transmitted to Ours Privacy as part of any
 * URL-shaped property (e.g. `current_url`, `pathname` on Virtual Page View).
 */
const SENSITIVE_URL_PARAMS = [
  "state",
  "iss",
  "code",
  "email",
  "payment_intent",
  "payment_intent_client_secret",
] as const;

/** Property keys that must never reach Ours — identity is sent via identify(). */
const PHI_KEYS = [
  "email",
  "firstName",
  "lastName",
  "phoneNumber",
  "name",
  "phone",
  "sponsoredEmail"
] as const;

/** Property keys that are known to be URL-shaped. */
const KNOWN_URL_FIELDS = [
  "current_url",
  "url",
  "path",
  "pathname",
  "referrer",
  "page_location"
];

/**
 * Deduped union of sensitive URL params and PHI keys — the canonical list of
 * query/hash parameter names that must be stripped from URL-shaped values
 * before they leave the client.
 *
 * Passed to the Ours SDK as `url_query_params_blocklist`, which is the sole
 * defense for the `current_url` and `referrer` the SDK captures itself on
 * autotracked events. The SDK strips these names from query strings and hash
 * fragments alike, and lowercases both sides when matching, so the camelCase
 * entries below also match `?firstname=` and `?FirstName=`.
 */
export const SENSITIVE_PARAM_SET: ReadonlySet<string> = new Set([
  ...SENSITIVE_URL_PARAMS,
  ...PHI_KEYS
]);

export type EventProps = Record<string, unknown>;

export function stripPhi(props: EventProps): EventProps {
  const out: EventProps = {};
  for (const key of Object.keys(props)) {
    if (!(PHI_KEYS as readonly string[]).includes(key)) {
      out[key] = props[key];
    }
  }
  return out;
}

/**
 * Matches absolute URLs (`https://...`, `http://...`) and root-relative
 * paths (`/foo`, `/foo?bar=1`, `/foo#bar`).
 */
const URL_PATTERN = /^(?:https?:\/\/|\/)/i;

function isUrlLike(value: unknown): value is string {
  return typeof value === "string" && URL_PATTERN.test(value);
}

function isPlainObject(value: unknown): value is EventProps {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Sanitizes an event payload before it leaves the client:
 *  - Top-level PHI keys are removed via `stripPhi` so `email`, `firstName`,
 *    etc. never reach Ours (identity is sent separately via `identify()`).
 *  - URL-shaped string values (absolute or root-relative) — and string
 *    values on known URL-named fields — are scrubbed via `sanitizeUrl`.
 *  - Plain-object values have PHI keys removed via `stripPhi`.
 *  - Objects that still contain nested objects are recursed into so deeper
 *    URLs and PHI keys are sanitized too.
 *  - All other values pass through unchanged.
 */
export function sanitizeURLAndStripPHI(props: EventProps): EventProps {
  // strip PHI from the top level
  const cleaned = stripPhi(props);

  const out: EventProps = {};
  for (const key of Object.keys(cleaned)) {
    const value = cleaned[key];
    // sanitize urls from known fields (only when actually a string)
    // and other url-like string values
    if (
      (KNOWN_URL_FIELDS.includes(key) && typeof value === "string") ||
      isUrlLike(value)
    ) {
      out[key] = sanitizeUrl(value as string);
    // recurse if it is a nested object
    } else if (isPlainObject(value)) {
      // recurse into nested objects
      out[key] = sanitizeURLAndStripPHI(value);
    // recurse if it is an array or object
    } else if (Array.isArray(value)) {
      out[key] = value.map(item => isPlainObject(item) ? sanitizeURLAndStripPHI(item) : item);
    } else {
      out[key] = value;
    }
  }
  return out;
}

function stripSensitive(input: string): string {
  if (!input) return input;
  const params = new URLSearchParams(input);
  let mutated = false;
  for (const name of SENSITIVE_PARAM_SET) {
    if (params.has(name)) {
      params.delete(name);
      mutated = true;
    }
  }
  if (!mutated) return input;
  return params.toString();
}

/**
 * Sanitizes a URL hash fragment (the part after `#`, without the leading `#`).
 *
 * Two shapes are supported:
 *  - Flat key=value pair string (e.g. `routeName=SignIn&code=…`) — typical
 *    OAuth implicit/PKCE callbacks that put params directly in the fragment.
 *  - Hash-routing path with its own query string (e.g.
 *    `/callback?code=…&state=…`) — common in SPAs using HashHistory routers,
 *    where the hash contains a path that can carry its own `?` query.
 *
 * Returns the input unchanged when no blocklisted params are found.
 */
function sanitizeHashFragment(hash: string): string {
  if (!hash) return hash;

  const queryStart = hash.indexOf("?");
  if (queryStart < 0) {
    return stripSensitive(hash);
  }

  const hashPath = hash.slice(0, queryStart);
  const hashQuery = hash.slice(queryStart + 1);
  const cleanedQuery = stripSensitive(hashQuery);
  if (cleanedQuery === hashQuery) return hash;
  return cleanedQuery ? `${hashPath}?${cleanedQuery}` : hashPath;
}

/**
 * Strips every param listed in `URL_BLOCKLIST` (sensitive URL params +
 * PHI keys) from the query string and hash fragment of a URL.
 *
 * Accepts:
 *  - absolute URLs (`https://x.com/path?state=abc#code=xyz`)
 *  - root-relative paths (`/path?email=foo`)
 *  - bare query strings or fragments
 *
 * Returns the input unchanged when no blocklisted params are present, or
 * when the value cannot be parsed as a URL or path.
 */
export function sanitizeUrl(value: string): string {
  if (!value) return value;

  // Absolute URL
  try {
    const url = new URL(value);
    let mutated = false;
    for (const name of SENSITIVE_PARAM_SET) {
      if (url.searchParams.has(name)) {
        url.searchParams.delete(name);
        mutated = true;
      }
    }
    if (url.hash.startsWith("#")) {
      const hashContent = url.hash.slice(1);
      const cleanedHash = sanitizeHashFragment(hashContent);
      if (cleanedHash !== hashContent) {
        url.hash = cleanedHash ? `#${cleanedHash}` : "";
        mutated = true;
      }
    }
    return mutated ? url.toString() : value;
  } catch {
    // Not an absolute URL — fall through to relative handling
  }

  // Path with optional query/hash
  const hashIdx = value.indexOf("#");
  const queryIdx = value.indexOf("?");

  let path = value;
  let query = "";
  let hash = "";

  if (hashIdx >= 0) {
    hash = value.slice(hashIdx + 1);
    path = value.slice(0, hashIdx);
  }
  if (queryIdx >= 0 && (hashIdx < 0 || queryIdx < hashIdx)) {
    query = path.slice(queryIdx + 1);
    path = path.slice(0, queryIdx);
  }

  const cleanedQuery = query ? stripSensitive(query) : "";
  const cleanedHash = hash ? sanitizeHashFragment(hash) : "";

  if (cleanedQuery === query && cleanedHash === hash) return value;

  let result = path;
  if (cleanedQuery) result += `?${cleanedQuery}`;
  if (cleanedHash) result += `#${cleanedHash}`;
  return result;
}

